import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { deleteRequest, postRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { LocaleContext } from '../../LocaleContext';
import UtilContext from '../../UtilContext';
import { runCrmPanelMutation, showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';

function userDisplayName(u) {
  return u?.employee?.name || u?.username || '';
}

function roleLabel(role, t) {
  switch (String(role || '').toLowerCase()) {
    case 'approval':
      return t('ServiceTaskDetail.Role.Approver');
    case 'execution':
      return t('ServiceTaskDetail.Role.Executor');
    case 'inspection':
      return t('ServiceTaskDetail.Role.Inspector');
    case 'management':
      return t('ServiceTaskDetail.Role.Manager');
    default:
      return '';
  }
}

/**
 * Zainteresované oddelenia / používatelia – parita ServiceTaskDetail (sidebar).
 */
export default function CrmCommunicationInterestedPartiesPanel({
  ticketId,
  ticket,
  ticketType = '',
  departmentRows = [],
  watcherParticipantRows = [],
  involvedDepartmentId = null,
  onChanged,
  readOnly = false,
  getUserColor,
  getUserInitials,
}) {
  const { t } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const [state] = useContext(DatabaseContext);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addDepartmentIds, setAddDepartmentIds] = useState([]);
  const [addUserIds, setAddUserIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const usersById = useMemo(() => {
    const map = new Map();
    (state?.users || []).forEach((u) => map.set(Number(u.id), u));
    return map;
  }, [state?.users]);

  const availableParticipantOptions = useMemo(() => {
    const taken = new Set((watcherParticipantRows || [])
      .filter((row) => Boolean(row?.involved) || String(row?.role || 'watcher').toLowerCase() === 'watcher')
      .map((row) => Number(row?.user_id)));
    return (state?.users || [])
      .filter((u) => !taken.has(Number(u?.id)))
      .map((u) => ({ label: userDisplayName(u), value: Number(u.id) }));
  }, [state?.users, watcherParticipantRows]);

  const availableDepartmentOptions = useMemo(() => {
    const taken = new Set((departmentRows || []).map((row) => Number(row?.department_id)));
    return (state?.departments || [])
      .filter((dep) => !taken.has(Number(dep?.id)))
      .map((dep) => ({ label: dep.name || `#${dep.id}`, value: Number(dep.id) }));
  }, [departmentRows, state?.departments]);

  const addInterestedUsers = useCallback(async () => {
    const ids = Array.isArray(addUserIds) ? addUserIds.filter(Boolean).map(Number) : [];
    if (!ticketId || ids.length === 0 || busy) return;
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        let lastRes = null;
        for (const userId of ids) {
          lastRes = await postRequest(`/api/crm_communication_tickets/${ticketId}/participants`, { user_id: userId, involved: true }, true);
          if (lastRes?.status !== 200) {
            throw new Error(String(lastRes?.message || JSON.stringify(lastRes?.errors || lastRes)));
          }
        }
        setAddUserIds([]);
        setShowAddUser(false);
        showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.UserAdded');
        return lastRes;
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  }, [addUserIds, busy, onChanged, t, ticketId, toast]);

  const addInterestedDepartments = useCallback(async () => {
    const ids = Array.isArray(addDepartmentIds) ? addDepartmentIds.filter(Boolean).map(Number) : [];
    if (!ticketId || ids.length === 0 || busy) return;
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        let lastRes = null;
        for (const departmentId of ids) {
          lastRes = await postRequest(`/api/crm_communication_tickets/${ticketId}/departments`, { department_id: departmentId }, true);
          if (lastRes?.status !== 200) {
            throw new Error(String(lastRes?.message || JSON.stringify(lastRes?.errors || lastRes)));
          }
        }
        setAddDepartmentIds([]);
        setShowAddDepartment(false);
        showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.DepartmentAdded');
        return lastRes;
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  }, [addDepartmentIds, busy, onChanged, t, ticketId, toast]);

  const removeInterestedDepartment = useCallback(async (departmentId) => {
    if (!ticketId || !departmentId || busy || readOnly) return;
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await deleteRequest(`/api/crm_communication_tickets/${ticketId}/departments/${departmentId}`);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.InvolvementRemoved');
          return res;
        }
        throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  }, [busy, onChanged, readOnly, t, ticketId, toast]);

  const removeInterestedUser = useCallback(async (userId) => {
    if (!ticketId || !userId || busy || readOnly) return;
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await deleteRequest(`/api/crm_communication_tickets/${ticketId}/participants/${userId}`);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.InvolvementRemoved');
          return res;
        }
        throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  }, [busy, onChanged, readOnly, t, ticketId, toast]);

  const deptTitle = t('ServiceTaskDetail.InterestedDepartments');
  const usersTitle = t('ServiceTaskDetail.InterestedUsers');

  return (
    <>
      <div className="flex flex-col mb-5">
        <label className="font-bold mb-1">{deptTitle}</label>
        {departmentRows.map((row) => {
          const depName = row?.department?.name || (state?.departments || []).find((dpt) => Number(dpt.id) === Number(row?.department_id))?.name || `#${row?.department_id}`;
          const isInvolvedDept = involvedDepartmentId != null && Number(row?.department_id) === involvedDepartmentId;
          return (
            <div className="flex items-center gap-2 text-gray-800 mb-2" key={String(row.id || row.department_id)}>
              <Avatar className="p-overlay-badge" title={depName} label={getUserInitials(depName)} shape="circle" style={{ backgroundColor: getUserColor(depName), color: '#ffffff', marginRight: '5px' }} />
              <div className="flex flex-col flex-1">
                <span>{depName}</span>
                {isInvolvedDept ? (
                  <span className="text-sm text-emerald-700">
                    {ticketType === 'announcement'
                      ? t('CrmCommunication.Participants.InvolvedInAnnouncement')
                      : (ticketType === 'acquisition'
                        ? t('CrmCommunication.Participants.InvolvedInAcquisition')
                        : t('CrmCommunication.Participants.InvolvedInQuote'))}
                  </span>
                ) : null}
              </div>
              {!readOnly && !isInvolvedDept ? (
                <button
                  type="button"
                  title={t('ServiceTaskDetail.Remove')}
                  className="text-red-600 hover:text-red-800 disabled:opacity-25"
                  disabled={busy}
                  onClick={() => void removeInterestedDepartment(Number(row?.department_id))}
                >
                  <i className="pi pi-times" />
                </button>
              ) : null}
            </div>
          );
        })}
        <div className="text-gray-800">
          <button
            type="button"
            onClick={() => { setShowAddDepartment(true); setShowAddUser(false); }}
            className="flex items-center gap-2 disabled:opacity-25"
            disabled={readOnly}
          >
            <i className="pi pi-plus mr-2 px-1" style={{ fontSize: '1.5rem' }} />
            {t('ServiceTaskDetail.AddDepartment')}
          </button>
        </div>
      </div>

      <div className="flex flex-col mb-5">
        <label className="font-bold mb-1">{usersTitle}</label>
        {watcherParticipantRows.map((row) => {
          const participantUser = row?.user || usersById.get(Number(row?.user_id));
          const name = userDisplayName(participantUser)
            || userDisplayName(usersById.get(Number(row?.user_id)))
            || `#${row?.user_id}`;
          const participantRole = String(row?.role || 'watcher').toLowerCase();
          const canRemoveParticipant = (participantRole === 'watcher' || Boolean(row?.involved))
            && Number(ticket?.created_by_user_id) !== Number(row?.user_id);
          const roleText = roleLabel(participantRole, t);
          return (
            <div className="flex items-center gap-2 text-gray-800 mb-2" key={String(row.id || row.user_id)}>
              <Avatar className="p-overlay-badge" title={name} label={getUserInitials(name)} shape="circle" style={{ backgroundColor: getUserColor(participantUser?.username ?? name), color: '#ffffff', marginRight: '5px' }} />
              <div className="flex flex-col flex-1">
                <span>{name}</span>
                {Number(ticket?.created_by_user_id) === Number(row?.user_id) ? <span className="text-sm text-gray-500">{t('ServiceTaskDetail.Founder')}</span> : null}
                {roleText ? <span className="text-sm text-gray-500">{roleText}</span> : null}
              </div>
              {!readOnly && canRemoveParticipant ? (
                <button
                  type="button"
                  title={t('ServiceTaskDetail.Remove')}
                  className="text-red-600 hover:text-red-800 disabled:opacity-25"
                  disabled={busy}
                  onClick={() => void removeInterestedUser(Number(row?.user_id))}
                >
                  <i className="pi pi-times" />
                </button>
              ) : null}
            </div>
          );
        })}
        <div className="text-gray-800">
          <button
            type="button"
            onClick={() => { setShowAddUser(true); setShowAddDepartment(false); }}
            className="flex items-center gap-2 disabled:opacity-25"
            disabled={readOnly}
          >
            <i className="pi pi-plus mr-2 px-1" style={{ fontSize: '1.5rem' }} />
            {t('ServiceTaskDetail.AddUser')}
          </button>
        </div>
      </div>
      <Dialog
        header={t('ServiceTaskDetail.AddDepartment')}
        visible={showAddDepartment}
        style={{ width: '35vw' }}
        onHide={() => setShowAddDepartment(false)}
      >
        <div className="flex flex-col gap-3">
          <MultiSelect
            value={addDepartmentIds}
            options={availableDepartmentOptions}
            onChange={(e) => setAddDepartmentIds(e.value || [])}
            placeholder={t('ServiceTaskDetail.SelectDepartment')}
            display="chip"
            filter
            className="w-full"
          />
          <button
            type="button"
            onClick={() => void addInterestedDepartments()}
            disabled={addDepartmentIds.length === 0 || busy}
            className="flex justify-center items-center h-9 w-full font-medium px-3 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25"
          >
            {t('ServiceTaskDetail.Involve')}
          </button>
        </div>
      </Dialog>
      <Dialog
        header={t('ServiceTaskDetail.AddUser')}
        visible={showAddUser}
        style={{ width: '35vw' }}
        onHide={() => setShowAddUser(false)}
      >
        <div className="flex flex-col gap-3">
          <MultiSelect
            value={addUserIds}
            options={availableParticipantOptions}
            onChange={(e) => setAddUserIds(e.value || [])}
            placeholder={t('ServiceTaskDetail.SelectUser')}
            display="chip"
            filter
            className="w-full"
          />
          <button
            type="button"
            onClick={() => void addInterestedUsers()}
            disabled={addUserIds.length === 0 || busy}
            className="flex justify-center items-center h-9 w-full font-medium px-3 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25"
          >
            {t('ServiceTaskDetail.Involve')}
          </button>
        </div>
      </Dialog>
    </>
  );
}
